import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineCog,
  HiOutlineSparkles,
} from "react-icons/hi";
import { FiArrowUpRight } from "react-icons/fi";
import CustomButton from "components/common/Button/CustomButton";

const copy = {
  title: "AI tools are now available in Vertex360",
  subtitle:
    "AI is now integrated across Vertex360, helping you work more efficiently with faster insights, smarter reviews, and clearer guidance.",
  aboutTitle: "About AI",
  aboutBody: [
    "AI in Vertex360 uses information already available within the platform to assist with everyday tasks, answer questions, perform checks, and support reviews.",
    "AI-generated results are provided as recommendations and insights for you to review and verify. No changes are made automatically to the system.",
    "The AI features available to you may vary depending on how AI has been configured for your organisation.",
  ],
  whatsIncluded: "What's included",
  featuresEmpty:
    "No AI modules are enabled for this organisation yet. When they are turned on, they will appear here.",
  remindLater: "Remind me later",
  gotIt: "Don't show again",
  turnOffTitle: "You can turn off AI features at any time",
  turnOffBody:
    "If you don't want your team to use AI-powered features, you can disable them in your AI Settings.",
  aiSettings: "AI Settings",
};

const FEATURE_ACCENT = {
  ai_timesheet: { wrap: "bg-[#EDE9FE]", icon: "text-[#6D28D9]" },
  ai_care_watch: { wrap: "bg-[#DCFCE7]", icon: "text-[#15803D]" },
  ai_copilot: { wrap: "bg-[#DBEAFE]", icon: "text-[#2563EB]" },
};

const DEFAULT_ACCENT = { wrap: "bg-[#E8EDFA]", icon: "text-[#4B6CB7]" };
const SCROLL_END_THRESHOLD_PX = 32;

const SubFeature = ({ title, detail }) => (
  <div className="flex items-start gap-2">
    <span className="mt-1.5 h-[5px] w-[5px] shrink-0 rounded-full bg-[#4B6CB7]" aria-hidden="true" />
    <div className="min-w-0">
      <p className="text-xs font-semibold leading-snug text-gray-700">{title}</p>
      {detail ? <p className="mt-0.5 text-xs leading-relaxed text-gray-700">{detail}</p> : null}
    </div>
  </div>
);

const FeaturesSkeleton = () => (
  <div className="space-y-2.5" aria-hidden="true">
    {[0, 1, 2].map((row) => (
      <div key={row} className="flex items-center gap-3 rounded-xl border border-[#E5E7EB] px-3.5 py-3.5">
        <div className="h-10 w-10 shrink-0 animate-pulse rounded-xl bg-gray-200" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-3 w-40 animate-pulse rounded bg-gray-200" />
          <div className="h-3 w-full animate-pulse rounded bg-gray-100" />
        </div>
      </div>
    ))}
  </div>
);

const FeatureCard = ({ feature, open, onToggle }) => {
  const accent = FEATURE_ACCENT[feature.key] || DEFAULT_ACCENT;
  const Icon = feature.Icon;
  const canExpand = feature.items.length > 0;
  const panelRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(0);
  const rowClass = "flex w-full items-center gap-3 px-3.5 py-3.5 text-left";

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) {
      return undefined;
    }
    setPanelHeight(open ? panel.scrollHeight : 0);
    return undefined;
  }, [open, feature.items.length]);

  const row = (
    <>
      <span
        className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${accent.wrap} ${accent.icon}`}
      >
        <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-gray-700">{feature.name}</span>
        {feature.description ? (
          <span className="mt-0.5 block text-xs leading-relaxed text-gray-700">
            {feature.description}
          </span>
        ) : null}
      </span>
      {canExpand ? (
        <HiOutlineChevronRight
          className={`h-4 w-4 shrink-0 text-[#94A3B8] transition-transform duration-300 ease-out ${open ? "rotate-90" : ""}`}
          aria-hidden="true"
        />
      ) : null}
    </>
  );

  return (
    <section className="overflow-hidden rounded-xl border border-[#E5E7EB] bg-white">
      {canExpand ? (
        <button
          type="button"
          className={rowClass}
          aria-expanded={open}
          onClick={onToggle}
        >
          {row}
        </button>
      ) : (
        <div className={rowClass}>{row}</div>
      )}
      {canExpand ? (
        <div
          className="overflow-hidden transition-[height] duration-300 ease-out"
          style={{ height: panelHeight }}
        >
          <div ref={panelRef} className="space-y-3 border-t border-[#E5E7EB] px-3.5 py-3 pl-[62px]">
            {feature.items.map((item) => (
              <SubFeature key={item.title} title={item.title} detail={item.detail} />
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
};

const AiEnablementNoticeModal = ({
  isOpen,
  onRemindLater,
  onGotIt,
  onOpenAiSettings,
  dismissing = false,
  features = [],
  featuresLoading = false,
}) => {
  const scrollRef = useRef(null);
  const [openFeatureKey, setOpenFeatureKey] = useState(null);
  const [hasReadToEnd, setHasReadToEnd] = useState(false);

  const remainingScroll = () => {
    const el = scrollRef.current;
    if (!el) {
      return 0;
    }
    return el.scrollHeight - el.scrollTop - el.clientHeight;
  };

  // Reaching the end reveals the footer. Scrolling back up does not hide it.
  const markReadIfAtEnd = useCallback(() => {
    if (remainingScroll() <= SCROLL_END_THRESHOLD_PX) {
      setHasReadToEnd(true);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setHasReadToEnd(false);
      setOpenFeatureKey(null);
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      setHasReadToEnd(remainingScroll() <= SCROLL_END_THRESHOLD_PX);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, featuresLoading, features.length]);

  const toggleFeature = (key) => {
    setOpenFeatureKey((current) => (current === key ? null : key));
  };

  const scrollContentDown = () => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    const step = Math.min(Math.max(remaining, 0), Math.max(el.clientHeight * 0.85, 160));
    el.scrollTo({ top: el.scrollTop + step, behavior: "auto" });
    if (el.scrollHeight - (el.scrollTop + step) - el.clientHeight <= SCROLL_END_THRESHOLD_PX) {
      setHasReadToEnd(true);
    }
  };

  const showScrollHint = isOpen && !hasReadToEnd && !featuresLoading;
  const showFooter = hasReadToEnd && !featuresLoading;

  return (
    <Transition.Root as={Fragment} show={isOpen}>
      <Dialog
        as="div"
        className="relative z-[10050]"
        onClose={() => { }}
        initialFocus={scrollRef}
        aria-labelledby="ai-enablement-notice-title"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[#0F172A]/60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="flex h-full min-h-0 items-center justify-center p-2 sm:p-4 md:p-5 lg:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-2"
            >
              <Dialog.Panel className="flex h-[min(760px,calc(100vh-2.5rem))] min-h-0 w-full max-w-[640px] flex-col overflow-hidden rounded-[18px] bg-white text-left shadow-[0_16px_40px_rgba(15,23,42,0.18)] outline-none focus:outline-none">
                <header className="flex shrink-0 items-start gap-3 bg-gradient-to-r from-[#4B6CB7] to-[#293F6A] px-4 py-4 sm:px-5 sm:py-5">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white">
                    <HiOutlineSparkles className="h-[18px] w-[18px]" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <Dialog.Title
                      id="ai-enablement-notice-title"
                      className="text-base font-semibold leading-snug text-white sm:text-lg capitalize"
                    >
                      {copy.title}
                    </Dialog.Title>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-[#DBEAFE]">
                      {copy.subtitle}
                    </p>
                  </div>
                </header>

                <div className="relative flex min-h-0 flex-1 flex-col">
                  <div
                    ref={scrollRef}
                    data-scroll-lock-allow=""
                    onScroll={markReadIfAtEnd}
                    onWheel={(event) => event.stopPropagation()}
                    tabIndex={0}
                    aria-label="AI enablement details"
                    className="min-h-0 flex-1 space-y-3.5 overflow-y-auto overscroll-contain px-4 py-3.5 text-gray-700 focus:outline-none sm:px-5 sm:py-4"
                  >
                    <section className="flex items-start gap-2.5 rounded-xl border border-[#E5E7EB] bg-white p-3.5">
                      <span className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-[#4B6CB7] text-sm font-bold text-white">
                        i
                      </span>
                      <div className="min-w-0">
                        <h2 className="text-sm font-semibold text-gray-700">{copy.aboutTitle}</h2>
                        {copy.aboutBody.map((paragraph) => (
                          <p key={paragraph} className="mt-1 text-[13px] leading-relaxed text-gray-700">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </section>

                    <h2 className="pt-1 text-base font-bold text-gray-700">{copy.whatsIncluded}</h2>

                    {featuresLoading ? (
                      <FeaturesSkeleton />
                    ) : features.length === 0 ? (
                      <p className="rounded-xl border border-[#E5E7EB] px-3.5 py-4 text-xs leading-normal text-gray-700">
                        {copy.featuresEmpty}
                      </p>
                    ) : (
                      <div className="space-y-2.5">
                        {features.map((feature) => (
                          <FeatureCard
                            key={feature.key}
                            feature={feature}
                            open={openFeatureKey === feature.key}
                            onToggle={() => toggleFeature(feature.key)}
                          />
                        ))}
                      </div>
                    )}

                    <section className="flex flex-col gap-3 rounded-xl bg-[#F4F6FB] p-3.5 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 items-start gap-3">
                        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#EDE9FE] text-[#6D28D9]">
                          <HiOutlineCog className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <div className="min-w-0">
                          <h2 className="text-sm font-semibold text-gray-700">{copy.turnOffTitle}</h2>
                          <p className="mt-1 text-xs leading-relaxed text-gray-700">{copy.turnOffBody}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={onOpenAiSettings}
                        className="inline-flex h-10 shrink-0 items-center justify-center gap-2 self-start rounded-lg border border-[#E5E7EB] bg-white px-3.5 text-sm font-semibold text-gray-700 transition hover:border-[#CBD5E1] hover:bg-[#F8FAFC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4B6CB7] focus-visible:ring-offset-2 sm:self-center"
                      >
                        <HiOutlineCog className="h-4 w-4 text-[#4B6CB7]" aria-hidden="true" />
                        {copy.aiSettings}
                        <FiArrowUpRight className="h-4 w-4 text-[#4B6CB7]" aria-hidden="true" />
                      </button>
                    </section>

                  </div>

                  {showScrollHint ? (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-white from-40% via-white/90 to-transparent px-5 pb-4 pt-14">
                      <div className="flex flex-col items-center">
                        <p className="mb-3 text-center text-sm font-semibold leading-snug text-gray-700">
                          Scroll down to read more
                        </p>
                        <button
                          type="button"
                          onClick={scrollContentDown}
                          className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-[#4B6CB7] text-white shadow-[0_6px_18px_rgba(75,108,183,0.55)] transition hover:bg-[#293F6A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4B6CB7] focus-visible:ring-offset-2"
                          aria-label="Scroll down to read more"
                        >
                          <HiOutlineChevronDown className="h-5 w-5 animate-bounce" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>

                {showFooter ? (
                  <footer className="grid shrink-0 grid-cols-1 gap-3 border-t border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 sm:grid-cols-2 sm:px-5 sm:py-4">
                    <CustomButton
                      type="button"
                      variant="secondary"
                      label={copy.remindLater}
                      clickHandler={onRemindLater}
                      isDisabled={dismissing}
                      cssClass="!h-[44px] !w-full !justify-center !py-0 !normal-case !border !border-solid !border-[#E5E7EB] !shadow-none !bg-white !text-gray-700 hover:!border-[#CBD5E1] hover:!bg-white hover:!text-gray-700 focus:!ring-0 focus:!ring-offset-0"
                    />
                    <CustomButton
                      type="button"
                      variant="primary"
                      label={copy.gotIt}
                      clickHandler={onGotIt}
                      isDisabled={dismissing}
                      showLoading={dismissing}
                      cssClass="!h-[44px] !w-full !justify-center !py-0 !normal-case !border-0 !shadow-none hover:!opacity-95 focus:!ring-0 focus:!ring-offset-0"
                    />
                  </footer>
                ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AiEnablementNoticeModal;
